import { forwardRef, PropsWithChildren, ReactNode } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import * as ScrollArea from '@radix-ui/react-scroll-area'
import * as Select from '@radix-ui/react-select'
import { cn } from 'shared/styles'

export const RegularSelectTrigger = forwardRef<
  HTMLButtonElement,
  Select.SelectTriggerProps
>(({ children, ...props }, forwardedRef) => (
  <Select.Trigger
    {...props}
    ref={forwardedRef}
  >
    {children}
  </Select.Trigger>
))

export const RegularSelectItemText = forwardRef<
  HTMLSpanElement,
  Select.SelectItemTextProps
>(({ children, ...props }, forwardedRef) => (
  <Select.ItemText
    {...props}
    ref={forwardedRef}
  >
    {children}
  </Select.ItemText>
))

export const RegularSelectItem = forwardRef<
  HTMLDivElement,
  Select.SelectItemProps
>(({ children, ...props }, forwardedRef) => (
  <Select.Item
    {...props}
    ref={forwardedRef}
  >
    {children}
  </Select.Item>
))

export const RegularSelectValue = forwardRef<
  HTMLDivElement,
  Select.SelectValueProps
>(({ children, ...props }, forwardedRef) => (
  <Select.Value
    {...props}
    ref={forwardedRef}
  >
    {children}
  </Select.Value>
))

export const RegularSelectIcon = forwardRef<
  HTMLSpanElement,
  Omit<Select.SelectIconProps, 'children'> & { children?: ReactNode }
>(({ children, ...props }, forwardedRef) => (
  <Select.Icon
    {...props}
    ref={forwardedRef}
  >
    {children ? (
      children
    ) : (
      <Select.Icon
        asChild
        className='size-6 transition group-aria-expanded:-scale-y-100'
      >
        <ChevronDownIcon />
      </Select.Icon>
    )}
  </Select.Icon>
))

type Props<T> = PropsWithChildren & {
  template?: (value?: T) => React.ReactNode
  value?: T
  isDisabled?: boolean
  isNullable?: boolean
  triggerClassName?: string
  optionsClassNames?: string
  placeholder?: string
  onChange: (newValue: T) => void
}

export function RegularSelect<T extends string>({
  value,
  onChange,
  template,
  isDisabled,
  isNullable,
  triggerClassName,
  optionsClassNames,
  children,
  placeholder,
}: Props<T>) {
  return (
    <Select.Root
      disabled={isDisabled}
      value={value}
      onValueChange={onChange}
    >
      {template ? (
        template(value)
      ) : (
        <RegularSelectTrigger
          className={cn(
            'base-input group flex size-full items-center justify-between border px-4 py-2 text-left transition *:first:truncate *:last:shrink-0',
            triggerClassName,
          )}
        >
          <RegularSelectValue placeholder={placeholder} />
          {isNullable && value ? (
            <div className='size-6' />
          ) : (
            <RegularSelectIcon />
          )}
        </RegularSelectTrigger>
      )}

      <Select.Portal>
        <Select.Content
          position='popper'
          className={cn(
            'animate-enterFromTop z-100 w-(--radix-select-trigger-width) rounded-md border bg-white py-2',
            optionsClassNames,
          )}
        >
          <ScrollArea.Root
            className='size-full'
            type='scroll'
          >
            <Select.Viewport asChild>
              <ScrollArea.Viewport
                className='size-full max-h-72'
                style={{ overflowY: undefined }}
              >
                {children}
              </ScrollArea.Viewport>
            </Select.Viewport>
            <ScrollArea.Scrollbar
              className='w-3 border-x border-transparent px-0.5 py-1'
              orientation='vertical'
            >
              <ScrollArea.Thumb className='rounded bg-gray-300' />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  )
}

type ItemsProps = {
  options: { value: string; label: string }[]
  selectItemClassNames?: string
}

export function RegularSelectItems({
  options,
  selectItemClassNames,
}: ItemsProps) {
  return options.map(({ label, value }) => (
    <RegularSelectItem
      className={cn(
        'truncate px-4 py-3 transition hover:bg-gray-200 data-[state=checked]:bg-gray-100',
        selectItemClassNames,
      )}
      key={value}
      value={value}
    >
      <RegularSelectItemText>{label}</RegularSelectItemText>
    </RegularSelectItem>
  ))
}
